import { useState, useEffect } from 'react';
import { IconAdjustmentsFilled, IconPlusFilled } from '@tabler/icons-react';
import Switch from '../Utils/Switch';
import ModalEliminarVariante from './ModalEliminarVariante';
import DeleteIconCircle from './DeleteIconCircle';
import { ensureMinLoading } from '../Utils/withLoading';
import { apiFetch } from '../api/client';

const FONT = '"Public Sans", sans-serif';

const inputClass =
  'w-full outline-none bg-white/[0.08] focus:bg-white/[0.12] focus:shadow-[inset_0_0_0_1px_rgba(255,255,255,0.16)] placeholder:text-white/30 transition-all duration-200 ease-out';

const inputStyle = {
  border: 'none',
  borderRadius: '16px',
  height: '48px',
  padding: '0 16px',
  fontSize: '15px',
  fontWeight: 500,
  fontFamily: FONT,
  color: 'rgba(255, 255, 255, 0.95)',
  width: '100%',
  boxSizing: 'border-box',
};

const labelStyle = {
  fontSize: '13px',
  lineHeight: '18px',
  fontWeight: 600,
  color: 'rgba(255, 255, 255, 0.45)',
  fontFamily: FONT,
};

const presetColors = [
  '#3B82F6', '#A78BFA', '#F97316', '#34D399',
  '#F472B6', '#22D3EE', '#FBBF24', '#EF4444',
];

export default function ModalEditarVariante({ isOpen, onClose, onUpdated, variant, dynamicIslandRef }) {
  const [form, setForm] = useState({
    nombre: '',
    requerido: false,
    color: presetColors[0],
    opciones: [],
  });
  const [removedOptionIds, setRemovedOptionIds] = useState([]);
  const [editedOptions, setEditedOptions] = useState({});
  const [newOptionName, setNewOptionName] = useState('');
  const [newOptionPrice, setNewOptionPrice] = useState('');
  const [saving, setSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    if (isOpen && variant) {
      setForm({
        nombre: variant.name || '',
        requerido: variant.is_required || false,
        color: variant.color || presetColors[0],
        opciones: (variant.options || []).map((o) => ({
          id: o.id,
          nombre: o.name,
          precioExtra: parseFloat(o.price_delta) || 0,
        })),
      });
      setRemovedOptionIds([]);
      setEditedOptions({});
      setNewOptionName('');
      setNewOptionPrice('');
      setSaving(false);
      setShowDeleteModal(false);
    }
  }, [isOpen, variant]);

  if (!isOpen || !variant) return null;

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const addOption = () => {
    if (newOptionName.trim()) {
      setForm((prev) => ({
        ...prev,
        opciones: [
          ...prev.opciones,
          { nombre: newOptionName.trim(), precioExtra: parseFloat(newOptionPrice) || 0 },
        ],
      }));
      setNewOptionName('');
      setNewOptionPrice('');
    }
  };

  const removeOption = (index) => {
    const opt = form.opciones[index];
    if (opt.id) {
      setRemovedOptionIds((prev) => [...prev, opt.id]);
    }
    setForm((prev) => ({
      ...prev,
      opciones: prev.opciones.filter((_, i) => i !== index),
    }));
  };

  const updateOptionField = (index, field, value) => {
    setForm((prev) => {
      const updated = [...prev.opciones];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, opciones: updated };
    });
    setEditedOptions((prev) => ({ ...prev, [index]: true }));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') e.target.blur();
  };

  const handleSave = async () => {
    if (!form.nombre.trim()) return;

    setSaving(true);
    dynamicIslandRef?.current?.addAlert('loading', 'Actualizando variante...');
    const startedAt = Date.now();

    try {
      const groupBody = {};
      if (form.nombre.trim() !== variant.name) groupBody.name = form.nombre.trim();
      if (form.requerido !== variant.is_required) groupBody.is_required = form.requerido;
      if (form.color !== variant.color) groupBody.color = form.color;

      if (Object.keys(groupBody).length > 0) {
        const groupRes = await apiFetch(`/variants/groups/${variant.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(groupBody),
        });
        if (!groupRes.ok) {
          dynamicIslandRef?.current?.clearAlerts();
          dynamicIslandRef?.current?.addAlert('error', 'Algo ha salido mal. Si el problema persiste, por favor reporta el error.');
          return;
        }
      }

      for (const optId of removedOptionIds) {
        await apiFetch(`/variants/options/${optId}`, { method: 'DELETE' });
      }

      for (const idxStr of Object.keys(editedOptions)) {
        const opt = form.opciones[parseInt(idxStr)];
        if (opt?.id) {
          await apiFetch(`/variants/options/${opt.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name: opt.nombre, price_delta: opt.precioExtra }),
          });
        }
      }

      const newOptions = form.opciones.filter((o) => !o.id);
      for (let i = 0; i < newOptions.length; i++) {
        const opt = newOptions[i];
        await apiFetch(`/variants/groups/${variant.id}/options`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: opt.nombre, price_delta: opt.precioExtra, sort_order: 999 }),
        });
      }

      await ensureMinLoading(startedAt);
      dynamicIslandRef?.current?.clearAlerts();
      dynamicIslandRef?.current?.addAlert('success', 'Variante actualizada correctamente');
      onUpdated?.();
    } catch (e) {
      dynamicIslandRef?.current?.clearAlerts();
      dynamicIslandRef?.current?.addAlert('error', 'Algo ha salido mal. Si el problema persiste, por favor reporta el error.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteGroup = async () => {
    setSaving(true);
    dynamicIslandRef?.current?.addAlert('loading', 'Eliminando variante...');
    const startedAt = Date.now();
    try {
      const res = await apiFetch(`/variants/groups/${variant.id}`, { method: 'DELETE' });
      if (!res.ok) {
        dynamicIslandRef?.current?.clearAlerts();
        dynamicIslandRef?.current?.addAlert('error', 'Error al eliminar la variante');
        return;
      }
      await ensureMinLoading(startedAt);
      dynamicIslandRef?.current?.clearAlerts();
      dynamicIslandRef?.current?.addAlert('success', 'Variante eliminada correctamente');
      setShowDeleteModal(false);
      onUpdated?.();
    } catch {
      dynamicIslandRef?.current?.clearAlerts();
      dynamicIslandRef?.current?.addAlert('error', 'Algo ha salido mal. Si el problema persiste, por favor reporta el error.');
    } finally {
      setSaving(false);
    }
  };

  const canSave = form.nombre.trim() && !saving;

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-3"
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.72)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
        }}
        onClick={onClose}
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="editar-variante-titulo"
          className="flex flex-col overflow-hidden w-full"
          style={{
            backgroundColor: '#000',
            borderRadius: '28px',
            border: '1px solid rgba(255, 255, 255, 0.16)',
            maxWidth: '880px',
            maxHeight: 'min(680px, calc(100dvh - 24px))',
            boxShadow: '0 40px 120px rgba(0, 0, 0, 0.8)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="shrink-0 px-5 sm:px-6 pt-5 sm:pt-6 pb-4 border-b border-white/[0.16]">
            <h2
              id="editar-variante-titulo"
              className="font-semibold m-0 truncate"
              style={{
                fontSize: '22px',
                lineHeight: '28px',
                letterSpacing: '-0.02em',
                color: 'rgba(255, 255, 255, 0.95)',
                fontFamily: FONT,
              }}
            >
              Editar variante
            </h2>
            <p
              className="m-0 mt-1 truncate"
              style={{
                fontSize: '13px',
                lineHeight: '18px',
                fontWeight: 500,
                color: 'rgba(255, 255, 255, 0.45)',
                fontFamily: FONT,
              }}
            >
              {variant.name} · {form.opciones.length} {form.opciones.length === 1 ? 'opción' : 'opciones'}
            </p>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto pedidos-scroll px-4 sm:px-6 py-5">
            <div className="flex flex-col md:flex-row gap-6">
              <div className="flex flex-col gap-5 flex-1" style={{ minWidth: 0 }}>
                <div className="flex flex-col gap-2">
                  <label htmlFor="editar-variante-nombre" style={labelStyle}>Nombre del grupo *</label>
                  <input
                    id="editar-variante-nombre"
                    type="text"
                    value={form.nombre}
                    onChange={(e) => handleChange('nombre', e.target.value)}
                    placeholder="Ej: Tamaño, Sabor, Temperatura"
                    className={inputClass}
                    style={inputStyle}
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <span style={labelStyle}>Color del badge</span>
                  <div className="flex gap-2.5 flex-wrap">
                    {presetColors.map((c) => {
                      const selected = form.color === c;
                      return (
                        <button
                          key={c}
                          type="button"
                          onClick={() => handleChange('color', c)}
                          aria-label={`Color ${c}`}
                          aria-pressed={selected}
                          className="shrink-0 rounded-full border-none cursor-pointer touch-manipulation transition-transform duration-200 ease-out active:scale-95"
                          style={{
                            width: '44px',
                            height: '44px',
                            backgroundColor: c,
                            transform: selected ? 'scale(1.05)' : 'scale(1)',
                            boxShadow: selected
                              ? '0 0 0 2px #000, 0 0 0 4px rgba(255, 255, 255, 0.9)'
                              : '0 0 0 1px rgba(255, 255, 255, 0.08)',
                          }}
                        />
                      );
                    })}
                  </div>
                </div>

                <div
                  className="flex items-center justify-between gap-4 min-h-[64px] px-4 rounded-2xl"
                  style={{ backgroundColor: 'rgba(255, 255, 255, 0.03)' }}
                >
                  <div className="flex flex-col min-w-0">
                    <span
                      style={{
                        fontSize: '14px',
                        lineHeight: '19px',
                        fontWeight: 600,
                        color: 'rgba(255, 255, 255, 0.9)',
                        fontFamily: FONT,
                      }}
                    >
                      Requerido
                    </span>
                    <span
                      style={{
                        fontSize: '12px',
                        lineHeight: '16px',
                        fontWeight: 500,
                        color: 'rgba(255, 255, 255, 0.4)',
                        fontFamily: FONT,
                      }}
                    >
                      El cliente debe elegir una opción
                    </span>
                  </div>
                  <Switch
                    showLabel={false}
                    defaultOn={form.requerido}
                    onChange={(val) => handleChange('requerido', val)}
                  />
                </div>
              </div>

              <div className="flex flex-col gap-3 flex-[1.4]" style={{ minWidth: 0 }}>
                <div className="flex items-center justify-between gap-3">
                  <span style={labelStyle}>Opciones</span>
                  <span
                    className="inline-flex items-center justify-center h-7 min-w-[28px] rounded-full px-2.5"
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      fontSize: '12px',
                      fontWeight: 600,
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: FONT,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {form.opciones.length}
                  </span>
                </div>

                <div className="flex flex-col gap-1.5 max-h-[280px] overflow-y-auto pedidos-scroll">
                  {form.opciones.length === 0 ? (
                    <div className="flex flex-col items-center justify-center gap-2 py-8 text-center">
                      <span
                        className="flex items-center justify-center w-11 h-11 rounded-2xl"
                        style={{ backgroundColor: 'rgba(255, 255, 255, 0.035)' }}
                      >
                        <IconAdjustmentsFilled size={21} color="rgba(255, 255, 255, 0.3)" />
                      </span>
                      <span
                        style={{
                          fontSize: '13px',
                          lineHeight: '18px',
                          fontWeight: 500,
                          color: 'rgba(255, 255, 255, 0.4)',
                          fontFamily: FONT,
                        }}
                      >
                        Agrega la primera opción
                      </span>
                    </div>
                  ) : (
                    form.opciones.map((opt, i) => (
                      <div
                        key={opt.id || `new-${i}`}
                        className="flex items-center gap-2 min-h-[56px] pl-2 pr-2 py-1.5 rounded-2xl"
                        style={{ backgroundColor: 'rgba(255, 255, 255, 0.025)' }}
                      >
                        <input
                          value={opt.nombre}
                          onChange={(e) => updateOptionField(i, 'nombre', e.target.value)}
                          onKeyDown={handleKeyDown}
                          aria-label={`Nombre de la opción ${i + 1}`}
                          className="flex-1 min-w-0 h-11 outline-none bg-white/[0.08] focus:bg-white/[0.12] transition-colors duration-200 placeholder:text-white/30"
                          style={{
                            border: 'none',
                            borderRadius: '12px',
                            padding: '0 12px',
                            fontSize: '14px',
                            fontWeight: 500,
                            fontFamily: FONT,
                            color: 'rgba(255, 255, 255, 0.95)',
                          }}
                        />
                        <span
                          className="shrink-0"
                          style={{ fontSize: '13px', fontWeight: 500, color: 'rgba(255, 255, 255, 0.35)', fontFamily: FONT }}
                        >
                          +$
                        </span>
                        <input
                          type="number"
                          value={opt.precioExtra}
                          onChange={(e) => updateOptionField(i, 'precioExtra', parseFloat(e.target.value) || 0)}
                          onKeyDown={handleKeyDown}
                          aria-label={`Precio extra de la opción ${i + 1}`}
                          className="shrink-0 h-11 text-center outline-none bg-white/[0.08] focus:bg-white/[0.12] transition-colors duration-200"
                          style={{
                            width: '68px',
                            border: 'none',
                            borderRadius: '12px',
                            padding: '0 8px',
                            fontSize: '14px',
                            fontWeight: 600,
                            fontFamily: FONT,
                            color: 'rgba(255, 255, 255, 0.95)',
                            fontVariantNumeric: 'tabular-nums',
                          }}
                        />
                        <DeleteIconCircle solid size={40} onClick={() => removeOption(i)} />
                      </div>
                    ))
                  )}
                </div>

                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newOptionName}
                    onChange={(e) => setNewOptionName(e.target.value)}
                    placeholder="Nombre de la opción"
                    aria-label="Nombre de la nueva opción"
                    onKeyDown={(e) => e.key === 'Enter' && addOption()}
                    className={inputClass}
                    style={{ ...inputStyle, flex: 1, height: '48px', fontSize: '14px' }}
                  />
                  <input
                    type="number"
                    value={newOptionPrice}
                    onChange={(e) => setNewOptionPrice(e.target.value)}
                    placeholder="+$0"
                    aria-label="Precio extra de la nueva opción"
                    onKeyDown={(e) => e.key === 'Enter' && addOption()}
                    className={`${inputClass} text-center`}
                    style={{ ...inputStyle, width: '88px', flexShrink: 0, height: '48px', fontSize: '14px' }}
                  />
                  <button
                    type="button"
                    onClick={addOption}
                    disabled={!newOptionName.trim()}
                    aria-label="Agregar opción"
                    className="flex items-center justify-center shrink-0 w-12 h-12 rounded-full border-none touch-manipulation select-none transition-colors duration-200 ease-out active:scale-[0.96] [@media(hover:hover)]:hover:bg-[#0077ed]"
                    style={{
                      backgroundColor: newOptionName.trim() ? '#fff' : 'rgba(255, 255, 255, 0.06)',
                      cursor: newOptionName.trim() ? 'pointer' : 'not-allowed',
                    }}
                  >
                    <IconPlusFilled
                      size={18}
                      color={newOptionName.trim() ? '#000' : 'rgba(255, 255, 255, 0.3)'}
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 shrink-0 px-4 sm:px-6 py-4 border-t border-white/[0.16]">
            <button
              type="button"
              onClick={() => setShowDeleteModal(true)}
              disabled={saving}
              className="flex items-center justify-center h-12 px-6 rounded-full border-none cursor-pointer touch-manipulation select-none transition-colors duration-200 ease-out active:scale-[0.98] [@media(hover:hover)]:hover:brightness-110"
              style={{
                backgroundColor: '#ff453a',
                fontSize: '14px',
                fontWeight: 600,
                letterSpacing: '-0.01em',
                color: '#fff',
                fontFamily: FONT,
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.5 : 1,
              }}
            >
              Eliminar
            </button>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="flex items-center justify-center h-12 px-6 rounded-full border-none cursor-pointer touch-manipulation select-none transition-colors duration-200 ease-out active:scale-[0.98] [@media(hover:hover)]:hover:bg-white/[0.85]"
                style={{
                  backgroundColor: '#fff',
                  fontSize: '14px',
                  fontWeight: 600,
                  letterSpacing: '-0.01em',
                  color: '#000',
                  fontFamily: FONT,
                  cursor: saving ? 'not-allowed' : 'pointer',
                  opacity: saving ? 0.5 : 1,
                }}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={!canSave}
                className="flex items-center justify-center h-12 px-7 rounded-full border-none cursor-pointer touch-manipulation select-none transition-colors duration-200 ease-out active:scale-[0.98] [@media(hover:hover)]:hover:bg-[#0077ed]"
                style={{
                  backgroundColor: canSave ? '#0071e3' : 'rgba(255, 255, 255, 0.06)',
                  fontSize: '15px',
                  fontWeight: 600,
                  letterSpacing: '-0.01em',
                  fontFamily: FONT,
                  color: canSave ? '#fff' : 'rgba(255, 255, 255, 0.35)',
                  cursor: canSave ? 'pointer' : 'not-allowed',
                }}
              >
                {saving ? 'Guardando…' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <ModalEliminarVariante
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteGroup}
        variantName={variant.name}
      />
    </>
  );
}
